import { getCountryCodeByTopLevelDomain } from '@/helpers';
import {
  CmsLink,
  PromoBanner,
  RewardsBanner,
  LoyaltyInterruptModal,
  LargeOrderPromo,
  LargeOrderPromoCard,
  JoinRewards,
  CmsLinkLocation,
  CmsLinkTarget,
  LegalCopy,
  UpsellItem,
  GuacMode,
  SystemFlags,
  FeaturedPCE,
  OrderWebMetadata,
  OrderWebTitleDescription,
  LegalCopyCreateAccount,
  PickupModality,
  CreateAccountSignInContentFragment,
  OrderConfirmationCreateAccountExperience,
} from '@/models/cms';
import { CountryCode } from '@/store/ui';
import axios from 'axios'


export function getPromoBanner(cmsBaseUrl: string, banner?: AemPromoBanner): PromoBanner | null {
  const content = banner && banner.master;
  if (!content) {
    return null;
  }
  const promo = {
    showPromoBanner: content.showPromoBanner || false,
    showAdaptiveHero: content.showAdaptiveHero ? content.showAdaptiveHero : false,
    nooHomePageURL: content.nooHomePageURL,
    showNewHero: content.showNewHero ? content.showNewHero : false,
    showHero: content.showHero ? content.showHero : false,
    dualPromo: content.dualPromo,
    centerAlignedTextOnly: content.centerAlignedTextOnly,
    isContentRightAligned: content.isContentRightAligned,
    shouldPlaceMobileTitleBelowImage: content.titleMainPlacement,
    primary: {
      backgroundImageUrl: content.backgroundImageUrl ? `${cmsBaseUrl}${content.backgroundImageUrl}` : undefined,
      backgroundVideoUrl: content.backgroundVideoUrl ? `${cmsBaseUrl}${content.backgroundVideoUrl}` : undefined,
      shouldLoopVideo: content.shouldLoopVideo,
      title: content.titleMain,
      preTitle: content.preTitleMain,
      preTitleColor: content.preTitleMainColor,
      titleColor: content.titleMainColor,
      subtitle: content.subtitleMain,
      subtitleColor: content.subtitleMainColor,
      legalText: content.legalTextMain,
      legalTextColor: content.legalTextColorMain,
      buttonText: content.buttonTextMain,
      buttonLink: content.buttonLinkMain,
      buttonColor: content.buttonColorMain,
      imageUrlDesktop: content.imageUrlMainDesktop ? `${cmsBaseUrl}${content.imageUrlMainDesktop}` : undefined,
      imageUrlMobile: content.imageUrlMainMobile ? `${cmsBaseUrl}${content.imageUrlMainMobile}` : undefined,
      videoUrlDesktop: content.videoUrlMainDesktop ? `${cmsBaseUrl}${content.videoUrlMainDesktop}` : undefined,
      videoUrlMobile: content.videoUrlMainMobile ? `${cmsBaseUrl}${content.videoUrlMainMobile}` : undefined,
      shouldLoopBottomVideo: content.shouldLoopMainVideo,
      linkCategory: content.linkCategoryMain
    },
  };
  // SecondaryButton split out to support two CTAs in primary hero mode
  const secondaryButton = {
    buttonText: content.buttonTextSecondary,
    buttonLink: content.buttonLinkSecondary,
    buttonColor: content.buttonColorSecondary,
  };
  const supportsSecondaryButton = secondaryButton.buttonText && secondaryButton.buttonLink;
  const secondary = {
    title: content.titleSecondary,
    titleColor: content.titleSecondaryColor,
    subtitle: content.subtitleSecondary,
    subtitleColor: content.subtitleSecondaryColor,
    legalText: content.legalTextSecondary,
    legalTextColor: content.legalTextColorSecondary,
    imageUrlMobile: content.imageUrlSecondaryMobile ? `${cmsBaseUrl}${content.imageUrlSecondaryMobile}` : undefined,
    imageUrlDesktop: content.imageUrlSecondaryDesktop ? `${cmsBaseUrl}${content.imageUrlSecondaryDesktop}` : undefined,
    videoUrlMobile: content.videoUrlSecondaryMobile ? `${cmsBaseUrl}${content.videoUrlSecondaryMobile}` : undefined,
    videoUrlDesktop: content.videoUrlSecondaryDesktop ? `${cmsBaseUrl}${content.videoUrlSecondaryDesktop}` : undefined,
    shouldLoopBottomVideo: content.shouldLoopSecondaryVideo,
    linkCategory: content.linkCategorySecondary,
    ...secondaryButton,
  };

  return promo.dualPromo ? { ...promo, secondary } : supportsSecondaryButton ? { ...promo, secondary: secondaryButton } : promo;
}

// TODO-7v3td2: remove this stub and add state directly to component
export function getHeaderLinks(cateringUrl: string): CmsLink[] {
  return [
    { type: 'text', text: 'Order', url: '/order/#menu', location: CmsLinkLocation.Internal, target: CmsLinkTarget.Self },
    { type: 'text', text: 'Catering', url: cateringUrl, location: CmsLinkLocation.External, target: CmsLinkTarget.Blank },
    { type: 'text', text: 'Rewards', url: '/order/rewards', location: CmsLinkLocation.Internal, target: CmsLinkTarget.Self },
    { type: 'text', text: 'Our Values', url: '/values', location: CmsLinkLocation.External, target: CmsLinkTarget.Self },
    { type: 'text', text: 'Nutrition', url: '/nutrition-calculator', location: CmsLinkLocation.Internal, target: CmsLinkTarget.Self },
  ];
}

export function getFeaturedHeader(featuredHeaderData: AemFeaturedHeader): string | null {
  const elements = featuredHeaderData && featuredHeaderData.master;
  if (!elements) {
    return null;
  }

  return elements.featuredTitle || null;
}

export function getGuacMode(guacmodeData?: AemGuacMode): GuacMode | null {
  return (guacmodeData && guacmodeData.master) || null;
}

export function getSignInModal(cmsBaseUrl?: string, { master: signInFrag }: AemAccountContentFragment = { master: { isModalEnabled: false } }, csSignInModal?: any | null): CreateAccountSignInContentFragment {
  if(csSignInModal && Object.keys(csSignInModal).length > 0){
    return signInFrag.isModalEnabled ? {
      isModalEnabled: signInFrag.isModalEnabled, //does not exist in cs gql fragment
      heading: csSignInModal?.heading,
      text1: csSignInModal?.text1,
      text2: csSignInModal?.text2,
      p1: signInFrag?.['p1@ContentType'], //does not exist in cs gql fragment
      p2: signInFrag?.['p2@ContentType'], //does not exist in cs gql fragment
      imageUrl: csSignInModal?.imageUrl ? `${cmsBaseUrl}${csSignInModal.imageUrl}` : undefined,
    } : signInFrag
  } else {
    return signInFrag.isModalEnabled ? {
      isModalEnabled: signInFrag.isModalEnabled,
      heading: signInFrag?.heading,
      text1: signInFrag?.text1,
      text2: signInFrag?.text2,
      p1: signInFrag?.['p1@ContentType'],
      p2: signInFrag?.['p2@ContentType'],
      imageUrl: signInFrag?.imageUrl ? `${cmsBaseUrl}${signInFrag.imageUrl}` : undefined,
    } : signInFrag
  }
}

export function getCreateAccountForm(cmsBaseUrl: string, { master: createAccountContent }: AemAccountContentFragment = { master: { isModalEnabled: false } }, csCreateAccountForm?: any | null): CreateAccountSignInContentFragment {
  if(csCreateAccountForm && Object.keys(csCreateAccountForm).length > 0){
    return createAccountContent?.isModalEnabled ? {
      isModalEnabled: createAccountContent?.isModalEnabled, //does not exist in cs gql fragment
      heading: csCreateAccountForm?.heading,
      subheading: csCreateAccountForm?.subheading,
      p1: createAccountContent?.['p1@ContentType'], //does not exist in cs gql fragment
      p2: createAccountContent?.['p2@ContentType'], //does not exist in cs gql fragment
      text1: csCreateAccountForm?.text1,
      buttonText: csCreateAccountForm?.buttonText,
      imageUrl: csCreateAccountForm?.imageUrl ? `${cmsBaseUrl}${csCreateAccountForm.imageUrl}` : undefined,
    } : { isModalEnabled: false, };
  } else {
    return createAccountContent?.isModalEnabled ? {
      isModalEnabled: createAccountContent?.isModalEnabled,
      heading: createAccountContent?.heading,
      subheading: createAccountContent?.subheading,
      p1: createAccountContent?.['p1@ContentType'],
      p2: createAccountContent?.['p2@ContentType'],
      text1: createAccountContent?.text1,
      buttonText: createAccountContent?.buttonText,
      imageUrl: createAccountContent?.imageUrl ? `${cmsBaseUrl}${createAccountContent.imageUrl}` : undefined,
    } : { isModalEnabled: false, };
  }
}

export function getFeaturedPCE(featuredPCEdata?: AemFeaturedPCE): FeaturedPCE {
  const PCEData = featuredPCEdata && featuredPCEdata.master;
  if (!PCEData) {
    return {
      header: 'FEATURED',
      subHeader: '',
      restaurantSelectedHeader: 'FEATURED',
    };
  }

  return {
    header: PCEData.header,
    subHeader: PCEData.subHeader,
    restaurantSelectedHeader: PCEData.restaurantSelectedHeader
  };
}

export function getRewardsBanner(cmsBaseUrl: string, rewardsBannerData?: AemRewardsBanner): RewardsBanner {
  const rewardsData = rewardsBannerData && rewardsBannerData.master;
  if (!rewardsData) {
    return {
      enableRewardsBanner: false,
      medallionImage: '',
      description: '',
      ctaSelecter: 'Standard',
      createAccountButtonLabel: '',
      dynamicCtaUrl: '',
      dynamicCtaButtonLabel: '',
      signInButtonLabel: '',
    };
  }

  return {
    enableRewardsBanner: rewardsData.enableRewardsBanner,
    medallionImage: `${cmsBaseUrl}${rewardsData.medallionImage}`,
    description: rewardsData.description,
    ctaSelecter: rewardsData.ctaSelecter,
    createAccountButtonLabel: rewardsData.createAccountButtonLabel,
    dynamicCtaUrl: rewardsData.dynamicCtaUrl,
    dynamicCtaButtonLabel: rewardsData.dynamicCtaButtonLabel,
    signInButtonLabel: rewardsData.signInButtonLabel,
  };
}

export function getLoyaltyInterruptModal(cmsBaseUrl: string, loyaltyInterruptModalData?: AemLoyaltyInterruptModal, csLoyaltyInterruptModal?: any | null): LoyaltyInterruptModal {
  if(csLoyaltyInterruptModal && Object.keys(csLoyaltyInterruptModal).length > 0){
    return {
      subCopy: csLoyaltyInterruptModal.subText,
      salutation: csLoyaltyInterruptModal.salutation,
      howRewardsWorksCta: csLoyaltyInterruptModal.howRewardsWorksCta,
      medallionImageUrl: `${cmsBaseUrl}${csLoyaltyInterruptModal.medallionImageUrl}`,
      mainCopy: csLoyaltyInterruptModal.mainText,
      joinNowCta: csLoyaltyInterruptModal.joinNowCta,
    };
  } else {
    const interruptData = loyaltyInterruptModalData && loyaltyInterruptModalData.master;
    if (!interruptData) {
      return {
        subCopy: `With Chipotle Rewards, you'll earn points to score FREE Chipotle.`,
        salutation: 'Welcome,',
        howRewardsWorksCta: 'HOW REWARDS WORKS',
        medallionImageUrl: `${cmsBaseUrl}/content/dam/chipotle/icons-and-animations/chipotle-pepper-icon/pepper-medallion.svg`,
        mainCopy: 'REWARD YOURSELF WITH FREE CHIPOTLE',
        joinNowCta: 'JOIN NOW',
      };
    }

    return {
      subCopy: interruptData.subCopy,
      salutation: interruptData.salutation,
      howRewardsWorksCta: interruptData.howRewardsWorksCta,
      medallionImageUrl: `${cmsBaseUrl}${interruptData.medallionImageUrl}`,
      mainCopy: interruptData.mainCopy,
      joinNowCta: interruptData.joinNowCta,
    };
  }
}

export function getLargeOrderPromo(cmsBaseUrl: string, largeOrderPromoData?: AemLargeOrderPromo): LargeOrderPromo | null {
  const content = largeOrderPromoData && largeOrderPromoData.master;
  if (!content) {
    return null;
  }

  return {
    mobileBackgroundImageUrl: `${cmsBaseUrl}${content.mobileBackgroundImageUrl}`,
    desktopBackgroundImageUrl: `${cmsBaseUrl}${content.desktopBackgroundImageUrl}`,
    mobileBackgroundColor: content.mobileBackgroundColor,
    desktopBackgroundColor: content.desktopBackgroundColor,
    preTitle: content.preTitle,
    preTitleColor: content.preTitleColor,
    title: content.title,
    titleColor: content.titleColor,
  };
}

export function getLargeOrderCard(cmsBaseUrl: string, groupOrderCardData?: AemLargeOrderCard): LargeOrderPromoCard | null {
  const content = groupOrderCardData && groupOrderCardData.master;
  if (!content) {
    return null;
  }

  return {
    title: content.title,
    titleColor: content.titleColor,
    backgroundColor: content.backgroundColor,
    imageUrl: `${cmsBaseUrl}${content.imageUrl}`,
    navUrl: content.navigationLink,
    detailsCopy: content.detailsCopy,
    detailsCopyColor: content.detailsCopyColor,
    boldCopy: content.boldCopy,
    boldCopyColor: content.boldCopyColor,
    buttonText: content.buttonText,
    buttonTextColor: content.buttonTextColor,
    buttonColor: content.buttonColor,
    linkCategory: content.linkCategory
  };
}

export function getJoinRewards(joinRewards?: AemJoinRewards, csJoinRewards?: any | null): JoinRewards | null {
  if(csJoinRewards && Object.keys(csJoinRewards).length > 0){
    return {
      header: csJoinRewards.header,
      buttonShortText: csJoinRewards.buttonShortText,
      buttonLongText: csJoinRewards.buttonLongText,
      emailUpdates: csJoinRewards.emailUpdates.html,
    };
  } else {
    const content = joinRewards && joinRewards.master;
    if (!content) {
      return null;
    }
  
    return {
      header: content.header,
      buttonShortText: content.buttonShortText,
      buttonLongText: content.buttonLongText,
      emailUpdates: content.emailUpdates,
    };
  }
}

export function getLegalCopy(legalCopy?: AemLegalCopy, csLegal?: any | null): LegalCopy | null {
  const countryCode = getCountryCodeByTopLevelDomain();
  if(csLegal && Object.keys(csLegal).length > 0){
    let content = csLegal; 
    let result: LegalCopy;
    let createAccountLegalCopy: any = {};
    let createAccountOrderConfirmationLegalCopy: any = {};
    createAccountLegalCopy[countryCode] = content.createAnAccount.html
    createAccountOrderConfirmationLegalCopy[countryCode] = content.createAnAccount.html
    result = {
      createAccount: createAccountLegalCopy,
      createAccountOrderConfirmation:createAccountOrderConfirmationLegalCopy,
      personalPreference: content.personalAndPreference.html,
      loggedInUnEnrolled: content.loggedInUnEnrolled.html,
      retroCredit: content.retroCredit.html
    }
    return result;
  } else {
    let content = legalCopy && legalCopy.master;
    if (!content) {
      return null;
    }

    const createAccountLegalCopy: LegalCopyCreateAccount = {
      US: content.createAccount,
      CA: content.createAccountCanada
    }
    const createAccountOrderConfirmationLegalCopy: LegalCopyCreateAccount = {
      US: content.createAccountOrderConfirmation,
      CA: content.createAccountOrderConfirmationCanada
    }
    let result: LegalCopy;
    switch (countryCode.toUpperCase()) {
      case CountryCode.CA:
        result = {
          createAccount: createAccountLegalCopy,
          createAccountOrderConfirmation: createAccountOrderConfirmationLegalCopy,
          personalPreference: content.personalPreferenceCanada,
          loggedInUnEnrolled: content.loggedInUnEnrolledCanada,
          retroCredit: content.retroCreditCanada
        }
        break;
      case CountryCode.US:
      default:
        result = {
          createAccount: createAccountLegalCopy,
          createAccountOrderConfirmation: createAccountOrderConfirmationLegalCopy,
          personalPreference: content.personalPreferenceUS,
          loggedInUnEnrolled: content.loggedInUnEnrolled,
          retroCredit: content.retroCredit
        }
        break;
    }
    return result;
  }
}

export function getDynamicUpsellItems(cmsBaseUrl: string, aemDynamicUpsell?: AemJSON  | null, upsellItems?: Array<any> | null): UpsellItem[] {
  if(upsellItems && upsellItems.length > 0){
    return upsellItems.map((item: any) => {
      item.imageUrl = `${cmsBaseUrl}${item.imageUrl?._path}`;
      item.shortName = item.MenuItemName;
      return item;
    })
  } else {
    if (!aemDynamicUpsell) {
      return [];
    }
  
    try {
      const jsonCleaned = new DOMParser().parseFromString(aemDynamicUpsell.master.json, 'text/html');
      const jsonParsed = JSON.parse(jsonCleaned.body.textContent || '');
  
      return jsonParsed.map((item: any) => {
        item.imageUrl = `${cmsBaseUrl}${item.imageUrl}`;
        return item;
      })
    } catch {
      // fallback values
      return [];
    }
  }
}

export function getSystemFlags(systemFlags: AemJSON): SystemFlags {
  try {
    const jsonCleaned = new DOMParser().parseFromString(systemFlags.master.json, 'text/html');
    return JSON.parse(jsonCleaned.body.textContent || '');
  } catch {
    // fallback values
    return {
      headerPages: ['/values', '/guacmode'],
    };
  }
}

export function getMetadata(metadata: AemMetadata): OrderWebMetadata | null {
  const content = metadata && metadata.master;
  if (!content) {
    return null;
  }

  return {
    title: content.googleSearchResultTitle,
    description: content.googleSearchResultDescription,
  };
}
export function getOrderWebTitleAndDescription(metadata: AemMetadata): OrderWebTitleDescription | null {
  const content = metadata && metadata.master;
  if (!content) {
    return null;
  }

  return {
    title: content.googleSearchResultTitle,
    description: content.googleSearchResultDescription,
  };
}

export function getNutritionPath(aemNutritionPath: AemNutritionPath): string | undefined {
  const content = aemNutritionPath && aemNutritionPath.master;
  if (!content) {
    return undefined;
  }

  return content.fullNutritionURL;
}

export function getPickupOptions(cmsBaseUrl: string, aemPickupOptions: AemPickupOptions, csPickupOptions?: Array<any> | null): PickupModality | undefined {
  if(csPickupOptions && csPickupOptions.length > 0){
    let options = csPickupOptions.map(option => {
      return {
        title: option.name,
        description: option.description,
        imageUrl: option.image._path
      }
    })
    return {
      dineInNotOpen: csPickupOptions[csPickupOptions.length -1].name,
      pickupOptions: options
    }
  }
  
  const options = aemPickupOptions?.master;
  if (!options) {
    return;
  }

  return {
    dineInNotOpen: options.Title5,
    pickupOptions: [
      {
        // Pickup
        title: options.Title1,
        description: options.Description1,
        imageUrl: `${cmsBaseUrl}${options.Image1}`,
      },
      {
        // Walk-up
        title: options.Title3,
        description: options.Description3,
        imageUrl: `${cmsBaseUrl}${options.Image3}`,
      },
      {
        // Chipotlane
        title: options.Title2,
        description: options.Description2,
        imageUrl: `${cmsBaseUrl}${options.Image2}`
      },
      {
        // Digital Kitchen
        title: options.Title4,
        description: options.Description4,
        imageUrl: `${cmsBaseUrl}${options.Image4}`,
      },
    ]
  };
}

export function getOrderConfirmationGuest(cmsBaseUrl: string, metadata: AEMGuestOrderCreateAccountFragment): OrderConfirmationCreateAccountExperience | null {
  const content = metadata && metadata.master;
  if (!content) {
    return null;
  }

  const orderConfirmationGuest = {
    header: content['header-text'],
    description: content['subtext-description'],
    medallionImageUrl: `${cmsBaseUrl}${content['medallion-image']}`
  };

  return orderConfirmationGuest;
}